import { computed, ref } from "vue";
import { isComposing } from "#ce/utils/events";
import { toBoolean } from "#ce/utils/props";

// The keys a shown list answers.
const OPEN_LIST_KEYS = ["ArrowUp", "ArrowDown", "Enter"];

// A row can hold a control of its own, such as a branch's disclosure button, which answers its
// keys itself.
const ROW_CONTROL = "button, a[href], input, select, textarea, summary";

function originatesFromRowControl(event) {
  const path = event.composedPath();
  const row = path.findIndex((node) => node?.localName === "sp-list-item");

  return row > 0 && path.slice(0, row).some((node) => node instanceof HTMLElement && node.matches(ROW_CONTROL));
}

// A consumer's slotted activator answers its own keys, such as a button's Enter that toggles the list.
function originatesFromSlottedActivator(event, hostElement) {
  const activator = hostElement?.querySelector(':scope > [slot="activator"]');

  return Boolean(activator) && event.composedPath().includes(activator);
}

/**
 * Marks the keys a shown list answers as handled, as a native select does, and tracks whether the
 * last input was a pointer press.
 *
 * `handleKeydown` and `handleClick` belong in the capture phase, so they run before a handler inside
 * closes the list. `leavesFocusOnClose` holds `no-restore-focus` only after a pointer press. A keyboard pick,
 * or a click no pointer press precedes, as a screen reader sends, still returns focus to the field.
 *
 * @param {object} props  Owning component's reactive props; reads `noRestoreFocus`.
 * @param {{ shown: import("vue").Ref<boolean>, hostElement?: HTMLElement | null }} options  `shown` is
 *   whether the list is shown. `hostElement` is the element whose slotted activator keeps its keys.
 * @returns {{ leavesFocusOnClose: import("vue").ComputedRef<boolean>,
 *   handleKeydown: (event: KeyboardEvent) => void, handlePointerdown: () => void,
 *   handleClick: (event: MouseEvent) => void }}
 */
export function useOpenListKeys(props, { shown, hostElement }) {
  const lastInputWasPointer = ref(false);

  const leavesFocusOnClose = computed(() => toBoolean(props.noRestoreFocus) && lastInputWasPointer.value);

  function handleKeydown(event) {
    lastInputWasPointer.value = false;

    if (
      shown.value &&
      OPEN_LIST_KEYS.includes(event.key) &&
      !isComposing(event) &&
      !event.composedPath().some((node) => node?.hasAttribute?.("data-selection-control")) &&
      !originatesFromRowControl(event) &&
      !originatesFromSlottedActivator(event, hostElement)
    ) {
      event.preventDefault();
    }
  }

  function handlePointerdown() {
    lastInputWasPointer.value = true;
  }

  // A click with no press count comes from the keyboard or assistive technology, not a pointer.
  function handleClick(event) {
    lastInputWasPointer.value = event.detail > 0;
  }

  return { leavesFocusOnClose, handleKeydown, handlePointerdown, handleClick };
}
